import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { isApiError } from "../../api/common";
import projectApi, { useByUserRoleUserList } from "../../api/project";
import { useContractorAccountList } from "../../api/utilities";
import { currentUserSelector } from "../../redux/slices/auth/selectors";
import { Loader, toast, toastError } from "../../common/components/core";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { FormSubmissionHandler, Party } from "../../types/common";
import { ProjectUserRole } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";

import ProjectForm, {
    ProjectFormValues,
    initialApprover,
} from "./components/ProjectForm";

const NewProjectView = (): JSX.Element => {
    document.title = PageTitle.NewProjectView;
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const currentUser = useSelector(currentUserSelector);
    const { data: contractors, isValidating: isContractorsLoading } =
        useContractorAccountList(currentUser?.userProfile.id);
    const { data: claimApprovers, isValidating: isClaimApproversLoading } =
        useByUserRoleUserList(ProjectUserRole.CLAIM_APPROVER, currentUser?.userProfile.id);
    const {
        data: paymentOrderApprovers,
        isValidating: isPaymentOrderApproversLoading,
    } = useByUserRoleUserList(ProjectUserRole.PAYMENT_ORDER_APPROVER, currentUser?.userProfile.id);

    const sortedContractorList: Party[] = useMemo(() => {
        if (contractors && contractors.length !== 0) {
            const contractorList = contractors.map((contractor) => {
                return {
                    name: contractor.name,
                    party: contractor.party,
                    accountName: contractor.accountName,
                };
            });
            return [...contractorList].sort((a, b) =>
                a.name?.localeCompare(b.name),
            );
        }
        return [];
    }, [contractors]);

    const handleSubmit: FormSubmissionHandler<ProjectFormValues> = async (
        values,
        { setErrors, setSubmitting },
    ) => {
        setSubmitting(true);
        try {
            await projectApi.create(values);
            toast(`Project '${values.name}' created`);
            history.push("/project");
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(onAuthError, (next) => (err) => {
                if (
                    isApiError(err) &&
                    err.errors[0]?.key === "Evm.Revert" && err.errors[0]?.enMessage?.includes("DuplicateProjectId") ){
                    toastError(
                        "Project ID already exists.",
                    );
                } else {
                    next(err);
                }
            })(e as GenericError);
        }
    };

    return (
        <Loader
            isLoading={
                isContractorsLoading ||
                isClaimApproversLoading ||
                isPaymentOrderApproversLoading
            }
            bgClassName="bg-body"
        >
            <ProjectForm
                onCancel={() => history.push("/project")}
                onSubmit={handleSubmit}
                contractors={sortedContractorList}
                claimApprovers={claimApprovers ?? []}
                paymentOrderApprovers={paymentOrderApprovers ?? []}
                isEdit={false}
                initialValues={{
                    name: "",
                    id: "",
                    contractor: [],
                    claimApprovers: [],
                    paymentOrderApprovers: [initialApprover],
                }}
            />
        </Loader>
    );
};

export default NewProjectView;
